import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LoadingState, Pill, ActionButton } from "@/components/shared";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { setPortalView } from "@/lib/portalApi";
import { PortalStatusIndicator } from "@/components/PortalStatusIndicator";
import WorkspaceTabs from "./workspace/WorkspaceTabs";
import WorkspaceNav from "./workspace/WorkspaceNav";
import AICommandCard from "@/components/AICommandCard";
import { OperationalCockpit } from "@/components/OperationalCockpit";
import { ResolveEstimateObligationDialog } from "@/components/ResolveEstimateObligationDialog";
import { ResolveDocumentBlockerPanel } from "@/components/ResolveDocumentBlockerPanel";
import CloseOutPanel from "@/components/closeout/CloseOutPanel";
import { CloseMatterDialog } from "@/components/CloseMatterDialog";
import { StrExternalDeliveryModal } from "@/components/str/StrExternalDeliveryModal";
import { useAuth } from "@/context/AuthContext";

// ─── Workspace Operational Error Boundary ────────────────────────────────────
// Wraps OperationalCockpit (Card 3 + cockpit rail). Any render exception in that
// surface is CONTAINED here — the workspace shell, header, and tabs remain usable.
// The boundary logs the full exception (observable) and offers a safe retry.
// NOTE: this boundary cannot know whether a prior save succeeded, failed, or whether
// the error is unrelated to any save action. The fallback uses only neutral language.
class WorkspaceOperationalBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the full exception so it remains observable/debuggable.
    console.error("[WorkspaceOperationalBoundary] Render exception captured:", error);
    console.error("[WorkspaceOperationalBoundary] Component stack:", errorInfo?.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
    // Trigger a fresh DRL + cockpit reload after reset.
    this.props.onRetry?.();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="workspace-operational-error-boundary"
          className="rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4 mb-4 flex items-start gap-3"
        >
          <Icons.AlertTriangle className="h-5 w-5 text-orange-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-orange-800 mb-1">
              Operational panel could not be rendered
            </p>
            <p className="text-xs text-orange-600">
              The operational panel encountered a display error. Your Client Workspace is still
              available. Use Retry to reload the current status, or refresh the page.
            </p>
          </div>
          <button
            data-testid="workspace-operational-error-retry"
            onClick={this.handleReset}
            className="shrink-0 text-xs font-semibold text-orange-700 border border-orange-300 bg-white hover:bg-orange-100 rounded-lg px-3 py-1.5 transition-colors"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const LIFECYCLE_TONES = { lead: "info", active_client: "success", inactive_client: "neutral", active: "success" };
const LIFECYCLE_LABELS = { lead: "Lead", active_client: "Active Client", inactive_client: "Inactive Client", active: "Active Client" };
const RISK_TONES = { low: "success", medium: "warning", high: "danger" };

// ─── Create Staff Task Modal ─────────────────────────────────────────────────
// Phase 1: wires the Client Actions "Create Staff Task" entry to the existing
// POST /crm/contacts/{clientId}/tasks endpoint.
// This is strictly a staff-internal task. The client never sees it.
function CreateStaffTaskModal({ clientId, clientName, currentUser, staffList, onClose, onCreated,
  // Phase 2A — optional structured context for routing (all nullable; defaults to generic client task)
  initialContextType, initialContextId, initialEngagementId }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState(currentUser?.id || "");
  const [priority, setPriority] = useState("normal");
  const [dueAt, setDueAt] = useState("");
  const [saving, setSaving] = useState(false);

  const assignable = (staffList || []).filter(s => s.status === "active" && !s.is_deleted);

  async function handleCreate() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/crm/contacts/${clientId}/tasks`, {
        title: title.trim(),
        description: description.trim() || undefined,
        assigned_to: assignedTo || undefined,
        priority,
        due_at: dueAt || undefined,
        // Phase 2A: pass context for canonical routing (omit undefined fields)
        ...(initialContextType ? { context_type: initialContextType } : {}),
        ...(initialContextId   ? { context_id:   initialContextId   } : {}),
        ...(initialEngagementId ? { engagement_id: initialEngagementId } : {}),
      });
      toast.success("Staff task created");
      onCreated();
      onClose();
    } catch {
      toast.error("Could not create task — please try again");
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.ClipboardCheck className="h-4 w-4 text-blue-600" />
            Create Staff Task
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Internal only — not visible to {clientName || "the client"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Title */}
          <div>
            <Label htmlFor="stask-title" className="text-sm font-medium">
              Task title <span className="text-red-500">*</span>
            </Label>
            <input
              id="stask-title"
              autoFocus
              value={title}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
              placeholder="e.g. Review uploaded 1099-R for discrepancy"
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="staff-task-title"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="stask-desc" className="text-sm font-medium">Description</Label>
            <Textarea
              id="stask-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Additional context (optional)"
              rows={2}
              className="mt-1.5 text-sm"
              data-testid="staff-task-description"
            />
          </div>

          {/* Assign to + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="stask-assignee" className="text-sm font-medium">Assign to</Label>
              <select
                id="stask-assignee"
                value={assignedTo}
                onChange={e => setAssignedTo(e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                data-testid="staff-task-assignee"
              >
                <option value="">— Unassigned —</option>
                {assignable.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.id === currentUser?.id ? " (you)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="stask-priority" className="text-sm font-medium">Priority</Label>
              <select
                id="stask-priority"
                value={priority}
                onChange={e => setPriority(e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                data-testid="staff-task-priority"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Due date */}
          <div>
            <Label htmlFor="stask-due" className="text-sm font-medium">Due date</Label>
            <input
              id="stask-due"
              type="date"
              value={dueAt}
              onChange={e => setDueAt(e.target.value)}
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="staff-task-due-date"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="staff-task-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!title.trim() || saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            data-testid="staff-task-submit"
          >
            {saving ? "Creating…" : "Create Staff Task"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Known workspace tab keys — used to validate ?tab= query param on entry.
// Listing them here (module level) prevents re-creation on every render.
const TAB_KEYS = ["overview", "tax", "services", "estimates", "engagement_letters", "engagements",
                  "invoices", "organizers", "production", "workflow", "communication", "documents", "timeline", "provenance"];

// ── STR Actions menu integration ─────────────────────────────────────────────
// Icon-only map keyed by action_code. All labels and routes come from
// project_str_state() (next_action_label, workspace_target, action_code).
const STR_ACTION_ICONS = {
  create_str_estimate:      "FilePlus2",
  open_str_workspace:       "Compass",
  publish_str_questionnaire:"Send",
  launch_str_analysis:      "Sparkles",
  view_str_progress:        "Activity",
  generate_str_draft:       "FileText",
  management_review:        "ShieldCheck",
  view_final_str_review:    "Eye",
  start_new_str_cycle:      "RotateCcw",
};

// Prefer workspace_target from the projection. Only override for actions that
// open the governed estimate workflow (no unconsumed query contract introduced).
function resolveStrTarget(strState, clientId) {
  const { action_code, workspace_target } = strState;
  if (action_code === "create_str_estimate" || action_code === "start_new_str_cycle") {
    return `/sales/estimates?create=1&client=${clientId}`;
  }
  return workspace_target || `/client-success/strategic-review/${clientId}`;
}

// V3-COMM-R3: single high-level governance signal (no counts). Click jumps to the provenance timeline.
const GOV_STATUS_UI = {
  all_governed: { tone: "success", icon: "ShieldCheck" },
  contains_legacy: { tone: "warning", icon: "ShieldAlert" },
  none: { tone: "neutral", icon: "Shield" },
};

function GovernanceStatusChip({ clientId, onOpen }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/communications/governance-status/${clientId}`)
      .then((r) => { if (alive) setS(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [clientId]);
  if (!s || s.status === "none") return null;
  const ui = GOV_STATUS_UI[s.status] || GOV_STATUS_UI.none;
  const Icon = Icons[ui.icon] || Icons.Shield;
  return (
    <button type="button" onClick={onOpen} data-testid="ws-governance-status-chip" title="View communication provenance">
      <Pill tone={ui.tone} className="cursor-pointer hover:opacity-80 inline-flex items-center gap-1">
        <Icon className="h-3 w-3" />{s.label}
      </Pill>
    </button>
  );
}

function HealthRing({ score = 0 }) {
  const r = 22, circ = 2 * Math.PI * r, off = circ - (score / 100) * circ;
  const color = score >= 80 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative h-14 w-14 shrink-0" data-testid="health-ring">
      <svg className="h-14 w-14 -rotate-90" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r={r} fill="none" stroke="#e2e8f0" strokeWidth="5" />
        <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={off} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-slate-800">{score}</span>
    </div>
  );
}

function HeaderMeta({ label, value, icon }) {
  const Icon = icon ? (Icons[icon] || Icons.Circle) : null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5 truncate">{Icon && <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />}{value || "—"}</div>
    </div>
  );
}

// Inline assignment popover — team or case manager, used in the workspace header.
// canEdit determines whether the field is interactive or read-only.
function AssignmentField({ label, icon, value, placeholder, options, onSave, canEdit, testid, saving }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const Icon = Icons[icon] || Icons.Circle;
  const filtered = (options || []).filter((o) =>
    !query.trim() || o.name.toLowerCase().includes(query.toLowerCase()) || (o.subtitle || "").toLowerCase().includes(query.toLowerCase())
  );

  const select = async (id, name) => {
    setOpen(false);
    setQuery("");
    await onSave(id, name);
  };

  if (!canEdit) {
    return (
      <div className="min-w-0" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5 truncate">
          <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />{value || "—"}
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          aria-label={`${value ? "Change" : "Assign"} ${label}`}
          className={cn(
            "min-w-0 text-left group rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold",
            "hover:bg-slate-100 cursor-pointer"
          )}
        >
          <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
          <div className="text-sm font-medium flex items-center gap-1.5">
            {saving ? (
              <Icons.Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin shrink-0" />
            ) : (
              <Icon className={cn("h-3.5 w-3.5 shrink-0", value ? "text-slate-400" : "text-blue-500")} />
            )}
            {value ? (
              <span className="text-slate-800 truncate">{value}</span>
            ) : (
              <span className="text-blue-600 font-medium">{placeholder}</span>
            )}
            <Icons.ChevronDown className="h-3 w-3 text-slate-300 group-hover:text-slate-500 shrink-0 ml-auto" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-64 p-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 px-2 pb-2">{label}</div>
        <div className="relative mb-1">
          <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            data-testid={`${testid}-search`}
            className="w-full rounded-md border border-slate-200 bg-slate-50 text-sm py-1.5 pl-8 pr-3 focus:outline-none focus:ring-2 focus:ring-brand-gold"
          />
        </div>
        <div className="max-h-52 overflow-y-auto tf-scroll">
          {value && (
            <button onClick={() => select(null, null)} data-testid={`${testid}-clear`}
              className="w-full text-left text-xs text-slate-400 hover:text-rose-600 px-2 py-1.5 rounded flex items-center gap-1.5">
              <Icons.X className="h-3.5 w-3.5" />Clear assignment
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="text-xs text-slate-400 px-2 py-3 text-center">No results</div>
          ) : (
            filtered.map((o) => (
              <button key={o.id} onClick={() => select(o.id, o.name)} data-testid={`${testid}-opt-${o.id}`}
                className={cn(
                  "w-full text-left rounded px-2 py-2 flex items-start gap-2 hover:bg-slate-100 transition-colors",
                  o.id === (value ? "current" : "") && "bg-slate-50"
                )}>
                <Icons.Check className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", value && o.name === value ? "text-emerald-500" : "text-transparent")} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{o.name}</div>
                  {o.subtitle && <div className="text-[11px] text-slate-400 truncate">{o.subtitle}</div>}
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


function KpiCard({ label, value, sub, tone = "slate", urgent }) {
  const tones = { slate: "text-slate-900", rose: "text-rose-600", amber: "text-amber-600", emerald: "text-emerald-600", blue: "text-blue-600", violet: "text-violet-600" };
  return (
    <div className={cn("bg-white border rounded-xl p-4 text-center", urgent ? "border-amber-300" : "border-slate-200")} data-testid={`ws-kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={cn("text-2xl font-heading font-semibold mt-1", tones[tone])}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ClientWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [drlData, setDrlData] = useState(null);
  const [documentBlockerPanelOpen, setDocumentBlockerPanelOpen] = useState(false);
  const [drlLoading, setDrlLoading] = useState(true); // true until first assessment returns
  const [showBriefing, setShowBriefing] = useState(false);
  const [showStrDialog, setShowStrDialog] = useState(false);
  const [showThaiTaxDialog, setShowThaiTaxDialog] = useState(false);
  const [convertedBanner, setConvertedBanner] = useState(false);
  const [related, setRelated] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [showResolveObligationDialog, setShowResolveObligationDialog] = useState(false);
  const [showCloseMatterDialog, setShowCloseMatterDialog] = useState(false);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteResult, setInviteResult] = useState(null);
  const [openComposer, setOpenComposer] = useState(false);
  // Estimate follow-up reminder confirm dialog
  const [followUpConfirm, setFollowUpConfirm] = useState(null);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  // Production organizer-override confirm dialog
  const [prodOverrideConfirm, setProdOverrideConfirm] = useState(null);
  const [prodOverrideBusy, setProdOverrideBusy] = useState(false);
  // Production handoff (CS → Production transfer) confirm dialog
  const [handoffConfirm, setHandoffConfirm] = useState(null); // { activationId, engagementId }
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [extCompleteConfirm, setExtCompleteConfirm] = useState(null); // { productionId }
  const [extCompleteBusy, setExtCompleteBusy] = useState(false);
  const [extCompleteForm, setExtCompleteForm] = useState({ completed_at: "", note: "" });
  // Close-out workflow state
  const [closeOutPanel, setCloseOutPanel] = useState(null); // { productionId } | null
  const [closeOutBusy, setCloseOutBusy] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocLoading, setPreviewDocLoading] = useState(false);
  // Create Engagement dialog
  const [showCreateEngDialog, setShowCreateEngDialog] = useState(false);
  const [engOptions, setEngOptions] = useState(null);
  const [engOptionsLoading, setEngOptionsLoading] = useState(false);
  // Portal View launch (workspace-native launch, mirrors PortalAdminCard)
  const [pvOpen, setPvOpen]     = useState(false);
  const [pvMode, setPvMode]     = useState("preview");
  const [pvReason, setPvReason] = useState("");
  const [pvBusy, setPvBusy]     = useState(false);
  // Create Staff Task modal (Phase 1)
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [teams, setTeams] = useState([]);
  const [savingTeam, setSavingTeam] = useState(false);
  const [savingManager, setSavingManager] = useState(false);
  // STR External Delivery modal — opens from Cockpit secondary CTA on eligible STR engagements
  const [showStrExternalDelivery, setShowStrExternalDelivery] = useState(false);
  const canAssign = user?.permission_keys?.includes("*") || user?.permission_keys?.includes("crm.edit");
  const isAdmin   = ["administrator", "partner"].includes(user?.role);
  // Gate C: use effective module membership — covers individual Finance grants (Sherry) as well as
  // role-based Finance access (administrator / partner / billing). Server is the authoritative boundary.
  const canViewFinance = user?.modules?.includes("finance");
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem(`tf.ws.lastTab.${id}`) || "overview"; } catch { return "overview"; }
  });
  // engSection drives the internal sub-navigation inside the Engagements workspace.
  // "overview" = engagements list; "letters" = engagement letters panel.
  const [engSection, setEngSection] = useState("overview");
  useEffect(() => {
    // On client change: restore localStorage tab first, then let the URL-param effect override if present.
    try { setTab(localStorage.getItem(`tf.ws.lastTab.${id}`) || "overview"); } catch { setTab("overview"); }
    setEngSection("overview");
  }, [id]);
  useEffect(() => {
    // URL-driven tab selection: ?tab=engagement_letters (or any known key) overrides localStorage.
    // Also handles ?action=assign_case_manager&engagement_id=xxx deep-links from LiveOps.
    // Validated against TAB_KEYS — unknown values fall back silently. Runs once per client (id dep).
    const tabFromUrl = searchParams.get("tab");
    const actionFromUrl = searchParams.get("action");
    if (tabFromUrl === "engagement_letters") {
      // Deep-link compatibility: redirect engagement_letters → engagements + letters sub-section.
      setTab("engagements");
      setEngSection("letters");
    } else if (tabFromUrl && TAB_KEYS.includes(tabFromUrl)) {
      setTab(tabFromUrl);
    } else if (actionFromUrl === "assign_case_manager") {
      // LiveOps deep-link: navigate to Engagements tab so the auto-open effect fires
      setTab("engagements");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Intentionally scoped to client change, not reactive to subsequent URL edits.
  useEffect(() => {
    try { localStorage.setItem(`tf.ws.lastTab.${id}`, tab); } catch { /* ignore */ }
  }, [tab, id]);

  const load = useCallback(async () => {
    try {
      const [b, r, d, t, a] = await Promise.allSettled([
        api.get(`/workspace/${id}`),
        api.get(`/crm/contacts/${id}/related`),
        api.get(`/workspace/${id}/documents`),
        api.get("/timeline", { params: { entity_type: "client", entity_id: id, limit: 50 } }),
        api.get(`/workspace/${id}/audit`),
      ]);
      // Only the primary workspace bundle is critical; CRM-related is supplementary.
      if (b.status === "rejected") throw b.reason;
      setBundle(b.value.data);
      setRelated(r.status === "fulfilled" ? r.value.data : null);
      setDocuments(d.status === "fulfilled" ? d.value.data : null);
      setTimeline(t.status === "fulfilled" ? t.value.data || [] : []);
      setAudit(a.status === "fulfilled" ? a.value.data || [] : []);
      // Canonical Operational Cockpit — the ONE authoritative operational summary (loaded
      // separately so the workspace still renders if the cockpit degrades).
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => setCockpit(null));
    } catch (e) {
      toast.error("Could not load workspace"); navigate("/client-workspace");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Canonical DRL assessment — one source of truth, shared between OperationalCockpit
  // (Card 3) and the DRLPanel below the tabs. One fetch, two presentations.
  // latestEngagementIdRef captures cockpit.primary_engagement_id without adding it
  // to runDrl's useCallback deps (avoids callback churn on cockpit refresh).
  const latestEngagementIdRef = useRef(null);
  useEffect(() => {
    latestEngagementIdRef.current = cockpit?.primary_engagement_id ?? null;
  }, [cockpit?.primary_engagement_id]);

  const runDrl = useCallback(async () => {
    if (!id) return;
    setDrlLoading(true);
    try {
      const engId = latestEngagementIdRef.current;
      const { data: r } = await api.post("/staff-assist/resolution-assessment", {
        client_id: id,
        ...(engId ? { engagement_id: engId } : {}),
      });
      setDrlData(r);
    } catch {
      setDrlData({ assessment_type: "error", error: "Assessment temporarily unavailable." });
    } finally { setDrlLoading(false); }
  }, [id]);

  useEffect(() => { if (id) runDrl(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run DRL once cockpit provides primary_engagement_id for the first time.
  // The initial runDrl() fires before cockpit is available (fail-safe: no suppression).
  // This corrects the assessment to the scoped result once engagement context is known.
  const drlScopedEngIdRef = useRef(null);
  useEffect(() => { drlScopedEngIdRef.current = null; }, [id]); // reset on client change
  useEffect(() => {
    const engId = cockpit?.primary_engagement_id;
    if (engId && drlScopedEngIdRef.current !== engId) {
      drlScopedEngIdRef.current = engId;
      runDrl();
    }
  }, [cockpit?.primary_engagement_id, runDrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a Resolve disposition: re-run DRL and refresh cockpit so Card 3 and Allison
  // panel advance together from the same updated state.
  const handleDrlRefresh = useCallback(async () => {
    await runDrl();
    api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
  }, [runDrl, id]);

  // Cockpit document-blocker action — fires when Card 3 shows a document_missing blocker
  // and staff clicks "Resolve Document Blocker".
  // Opens the governed ResolveDocumentBlockerPanel slide-over.
  // After every panel mutation, the panel calls runDrl() + onRefreshCockpit() itself.
  const handleDocumentBlocker = useCallback(() => {
    setDocumentBlockerPanelOpen(true);
  }, []);

  // Cockpit refresh callback — called by ResolveDocumentBlockerPanel after each mutation.
  // Refreshes DRL presentation, Cockpit canonical state, and Journey (bundle.intelligence).
  const handleCockpitRefreshFromBlockerPanel = useCallback(() => {
    runDrl();
    api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
    load(); // Journey data lives in bundle.intelligence; must refresh after every blocker mutation
  }, [runDrl, id, load]);

  // Load assignment lookups — teams and staff directory.
  // These are independent of the main workspace data and don't block rendering.
  useEffect(() => {
    if (!canAssign) return;
    api.get("/teams").then((r) => setTeams((r.data || []).filter((t) => t.active !== false))).catch(() => {});
    api.get("/crm/staff-directory").then((r) => setStaffList(r.data || [])).catch(() => {});
  }, [canAssign]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent lifecycle refresh — triggered by cross-tab or same-tab estimate acceptance events.
  // Does not set loading=true so the workspace does not flash/re-render headers.
  const silentRefetch = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        api.get(`/workspace/${id}`),
        api.get("/timeline", { params: { entity_type: "client", entity_id: id, limit: 50 } }),
      ]);
      setBundle((prev) => {
        const wasLead = prev?.contact?.lifecycle_status === "lead";
        const isNowClient = b.data?.contact?.lifecycle_status === "active_client";
        if (wasLead && isNowClient) setConvertedBanner(true);
        return b.data;
      });
      setTimeline(t.data || []);
    } catch { /* silent — workspace was already loaded */ }
  }, [id]);

  useEffect(() => {
    const onLifecycleUpdated = (e) => {
      if (e.detail?.clientId === id) silentRefetch();
    };
    const onStorage = (e) => {
      if (e.key === `tf.ws.lifecycle-update.${id}`) silentRefetch();
    };
    window.addEventListener("client:lifecycle-updated", onLifecycleUpdated);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("client:lifecycle-updated", onLifecycleUpdated);
      window.removeEventListener("storage", onStorage);
    };
  }, [id, silentRefetch]);

  // STR Actions menu integration: fetch project_str_state() non-blocking.
  // Resets on client change; aborts on unmount. 403/404 silently omit the section.
  const [strState, setStrState] = useState(null);

  // ── Inline assignment handlers ────────────────────────────────────────────
  const saveTeam = async (teamId) => {
    setSavingTeam(true);
    const prev = bundle?.contact?.assigned_team_name;
    try {
      await api.patch(`/crm/contacts/${id}/assignment`, { team_id: teamId ?? null });
      await load();
      toast.success(teamId ? "Team assigned" : "Team cleared");
    } catch {
      toast.error("Could not update team");
      // load() not called — prev value preserved in bundle
    } finally { setSavingTeam(false); }
  };

  const saveManager = async (managerId) => {
    setSavingManager(true);
    try {
      await api.patch(`/crm/contacts/${id}/assignment`, { manager_id: managerId ?? null });
    } catch {
      toast.error("Could not update case manager");
      setSavingManager(false);
      return;
    }
    try {
      await load();
      toast.success(managerId ? "Case manager assigned" : "Case manager cleared");
    } catch {
      // Write succeeded but workspace refresh failed — do not show a success toast.
      toast.error("Assignment saved, but workspace could not refresh. Please reload.");
    } finally { setSavingManager(false); }
  };

  useEffect(() => {
    const controller = new AbortController();
    setStrState(null);
    api.get(`/strategic-review/state/${id}`, { signal: controller.signal })
      .then((r) => setStrState(r.data))
      .catch((e) => {
        if (e?.name !== "CanceledError" && e?.name !== "AbortError") setStrState(null);
      });
    return () => controller.abort();
  }, [id]);

  // Thai Tax workspace state — reuses existing organizer query, non-blocking.
  const [thaiTaxState, setThaiTaxState] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    setThaiTaxState(null);
    api.get(`/organizers/thai-tax-state/${id}`, { signal: controller.signal })
      .then((r) => setThaiTaxState(r.data))
      .catch((e) => {
        if (e?.name !== "CanceledError" && e?.name !== "AbortError") setThaiTaxState(null);
      });
    return () => controller.abort();
  }, [id]);
  useEffect(() => {
    if (!bundle?.contact) return;
    try {
      const ct = bundle.contact;
      const entry = { id, name: ct.full_name || "Unnamed", number: ct.client_number || "",
                      lifecycle: ct.lifecycle_status || "", at: Date.now() };
      const prev = JSON.parse(localStorage.getItem("tf.ws.recent") || "[]").filter((x) => x.id !== id);
      localStorage.setItem("tf.ws.recent", JSON.stringify([entry, ...prev].slice(0, 8)));
    } catch { /* ignore */ }
  }, [bundle, id]);
  useEffect(() => {
    const h = () => setTab("communication");
    window.addEventListener("open-comm-center", h);
    return () => window.removeEventListener("open-comm-center", h);
  }, []);

  // Resolve engagement PDF document ID when the send_engagement dialog opens.
  // Reuses engagement data from the action object if unsigned_document_id is present;
  // falls back to a single API fetch only when it is absent.
  useEffect(() => {
    if (followUpConfirm?.type === "send_engagement" && followUpConfirm.engagementId) {
      // If the CTA already carried the document ID, use it immediately.
      if (followUpConfirm.unsignedDocumentId) {
        setPreviewDocId(followUpConfirm.unsignedDocumentId);
        return;
      }
      setPreviewDocId(null);
      setPreviewDocLoading(true);
      api.get(`/engagements/${followUpConfirm.engagementId}`)
        .then((r) => setPreviewDocId(r.data?.unsigned_document_id || null))
        .catch(() => setPreviewDocId(null))
        .finally(() => setPreviewDocLoading(false));
    } else {
      setPreviewDocId(null);
      setPreviewDocLoading(false);
    }
  }, [followUpConfirm?.type, followUpConfirm?.engagementId]);

  if (loading) return <LoadingState label="Loading client workspace…" />;
  if (!bundle) return null;

  const c = bundle.contact;
  const intel = bundle.intelligence || {};
  const wf = bundle.workflow;
  const initials = (c.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const quickAction = (msg) => toast.info(msg);

  const sendEstimateReminder = async () => {
    if (!followUpConfirm?.estimateId) return;
    setFollowUpBusy(true);
    try {
      const r = await api.post(`/sales/estimates/${followUpConfirm.estimateId}/send-reminder`);
      const d = r.data;
      // Handle informational "already accepted" — backend returns HTTP 200 with ok:false
      if (d.ok === false) {
        toast.info(d.message || "No reminder sent — estimate state does not require one.");
        setFollowUpConfirm(null);
        return;
      }
      const emailOk = d.email_status === "sent" || d.email_status === "queued";
      toast.success(
        emailOk
          ? `Reminder sent · Portal notification created`
          : `Reminder recorded (email: ${d.email_status || "skipped"})`,
        { duration: 6000 }
      );
      setFollowUpConfirm(null);
      // Refresh cockpit + timeline so the reminder is reflected immediately
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
      api.get("/timeline", { params: { entity_type: "client", entity_id: id, limit: 50 } })
        .then((r) => setTimeline(r.data || [])).catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.warning(detail || "A reminder was sent recently — please wait before sending another.");
        setFollowUpConfirm(null);
      } else {
        toast.error(detail || "Failed to send reminder — please try again.");
      }
    } finally { setFollowUpBusy(false); }
  };

  const sendEngagementLetterReminder = async () => {
    if (!followUpConfirm?.engagementId) return;
    setFollowUpBusy(true);
    try {
      const r = await api.post(`/engagements/${followUpConfirm.engagementId}/remind`, { status: "manual" });
      const d = r.data;
      const emailOk = d.email_status === "sent" || d.email_status === "queued";
      toast.success(
        emailOk
          ? `Engagement letter reminder sent`
          : `Reminder recorded (email: ${d.email_status || "skipped"})`,
        { duration: 5000 }
      );
      setFollowUpConfirm(null);
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
      api.get("/timeline", { params: { entity_type: "client", entity_id: id, limit: 50 } })
        .then((r) => setTimeline(r.data || [])).catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Failed to send reminder — please try again.");
    } finally { setFollowUpBusy(false); }
  };

  const prepareEngagementLetter = async () => {
    if (!followUpConfirm?.estimateId) return;
    setFollowUpBusy(true);
    try {
      const r = await api.post(`/sales/estimates/${followUpConfirm.estimateId}/prepare-engagement`);
      const d = r.data;
      if (d.sent) {
        toast.success("Engagement letter prepared and sent for e-signature.", { duration: 6000 });
      } else {
        toast.success(
          d.engagement_status === "generated" || d.engagement_status === "draft"
            ? "Engagement letter prepared — use 'Send Engagement Letter' when ready to dispatch."
            : d.message || "Engagement letter prepared.",
          { duration: 6000 }
        );
      }
      setFollowUpConfirm(null);
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
      api.get("/timeline", { params: { entity_type: "client", entity_id: id, limit: 50 } })
        .then((r) => setTimeline(r.data || [])).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to prepare engagement letter — please try again.");
    } finally { setFollowUpBusy(false); }
  };

  const sendEngagementLetterDirect = async () => {
    if (!followUpConfirm?.engagementId) return;
    setFollowUpBusy(true);
    try {
      const r = await api.post(`/engagements/${followUpConfirm.engagementId}/send`);
      const d = r.data;
      const emailOk = d.email_status === "sent" || d.email_status === "queued";
      toast.success(
        emailOk
          ? "Engagement letter sent for e-signature."
          : `Engagement letter dispatched (email: ${d.email_status || "skipped"})`,
        { duration: 5000 }
      );
      setFollowUpConfirm(null);
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
      api.get("/timeline", { params: { entity_type: "client", entity_id: id, limit: 50 } })
        .then((r) => setTimeline(r.data || [])).catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Failed to send engagement letter — please try again.");
    } finally { setFollowUpBusy(false); }
  };

  // Cockpit primary CTA — executes the structured action contract returned by the cockpit endpoint.
  // It must ALWAYS launch, navigate, or explain — never appear clickable while doing nothing.
  const handleCockpitAction = (na) => {
    const cta = na?.cta || {};
    if (cta.enabled === false) { toast.warning(cta.notice || "This action isn't available yet."); return; }
    if (cta.notice) toast.warning(cta.notice, { duration: 7000 });
    // Estimate follow-up: mutation action — show confirm dialog, never navigate to portal
    if (cta.action_type === "mutation" && cta.action_key === "follow_up_estimate") {
      setFollowUpConfirm({ type: "estimate", estimateId: cta.estimate_id, clientName: c?.full_name, na });
      return;
    }
    // Engagement letter follow-up: mutation action — show confirm dialog, never navigate to portal
    if (cta.action_type === "mutation" && cta.action_key === "follow_up_engagement_letter") {
      setFollowUpConfirm({ type: "engagement", engagementId: cta.engagement_id, clientName: c?.full_name, na });
      return;
    }
    // Prepare engagement letter: initiate canonical engagement onboarding from accepted estimate
    if (cta.action_type === "mutation" && cta.action_key === "prepare_engagement_letter") {
      setFollowUpConfirm({ type: "prepare_engagement", estimateId: cta.estimate_id, isHistorical: cta.is_historical, clientName: c?.full_name, na });
      return;
    }
    // Send a drafted-but-unsent engagement letter directly
    if (cta.action_type === "mutation" && cta.action_key === "send_engagement_letter") {
      setFollowUpConfirm({ type: "send_engagement", engagementId: cta.engagement_id, clientName: c?.full_name, na });
      return;
    }
    // Production organizer override: proceed to production while organizer remains open
    if (cta.action_type === "mutation" && cta.action_key === "proceed_production_organizer_override") {
      setProdOverrideConfirm({ engagementId: cta.engagement_id });
      return;
    }
    // Production handoff: execute canonical CS→Production transfer then open Production tab
    if (cta.action_type === "mutation" && cta.action_key === "complete_production_handoff") {
      setHandoffConfirm({ activationId: cta.activation_id, engagementId: cta.engagement_id });
      return;
    }
    // External production completion: record that work was done outside Tax Flow
    if (cta.action_type === "mutation" && cta.action_key === "record_production_external") {
      const today = new Date().toISOString().slice(0, 10);
      setExtCompleteForm({ completed_at: today, note: "" });
      setExtCompleteConfirm({ productionId: cta.production_id });
      return;
    }
    // STR external delivery: open the Upload Completed STR modal from the Cockpit secondary CTA.
    // The same handler is reached for all delivery states (upload / resume / status view):
    // the modal loads current state from the API and renders the appropriate view.
    if (cta.action_type === "mutation" && cta.action_key === "open_str_external_delivery") {
      setShowStrExternalDelivery(true);
      return;
    }
    // Close-out initiate: POST to initiate endpoint, then reload cockpit
    if (cta.action_type === "post_mutation" && (na?.action_code === "start_close_out" || cta.action_key === "start_close_out")) {
      const target = cta.action_target;
      if (!target) { toast.error("No close-out endpoint configured."); return; }
      setCloseOutBusy(true);
      api.post(target.replace(/^\/api/, ""), {})
        .then(() => { toast.success("Close-out initiated."); load(); })
        .catch((err) => {
          const detail = err?.response?.data?.detail;
          toast.error(detail ? `Could not initiate close-out: ${detail}` : "Could not initiate close-out.");
        })
        .finally(() => setCloseOutBusy(false));
      return;
    }
    // Close-out deliverables: open the deliverables confirmation panel
    if (na?.action_code === "upload_close_out_deliverables") {
      setCloseOutPanel({ productionId: na?.cta?.production_id || na?.production_id });
      return;
    }
    // Close-out retry (closing letter or review request): POST confirm-deliverables with empty body
    if (na?.action_code === "retry_closing_letter" || na?.action_code === "retry_review_request") {
      const pid = na?.cta?.production_id || na?.production_id;
      if (!pid) { toast.error("No production ID for retry."); return; }
      setCloseOutBusy(true);
      api.post(`/production/records/${pid}/close-out/confirm-deliverables`, { document_ids: [] })
        .then((r) => {
          if (r.data?.status === "payment_required") {
            toast.warning("A payment obligation must be satisfied before completing close-out.");
          } else {
            toast.success("Close-out communications retried.");
          }
          load();
        })
        .catch(() => toast.error("Retry failed. Please try again."))
        .finally(() => setCloseOutBusy(false));
      return;
    }
    // STR engagement letter actions: switch to the Engagements tab / Letters sub-section directly.
    // navigate() alone is insufficient here because the tab initialisation useEffect is intentionally
    // [id]-scoped and will not re-run when only the query param changes on the same page.
    const strActionCode = cta.action_code || na.action_code;
    if (strActionCode === "prepare_engagement_letter" || strActionCode === "resend_engagement_letter") {
      setTab("engagements");
      setEngSection("letters");
      if (!cta.notice) toast.success("Opening Engagement Letters…");
      setTimeout(() => document.getElementById("ws-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      return;
    }
    // Stage 2: resolve_unavailable_documents → open the Document Resolver panel directly.
    // The panel auto-materialises unavailable_document blocker items on load (withRefresh=true).
    if ((cta.action_code || na.action_code) === "resolve_unavailable_documents") {
      setDocumentBlockerPanelOpen(true);
      return;
    }
    const target = cta.action_target;
    if (cta.action_type === "open_briefing" || (!target && !cta.notice)) { setShowBriefing(true); return; }
    // Guard: emitted action has no action_target — surfaces as a visible error instead of a silent no-op.
    if (!target) {
      console.error("[cockpit] no action_target for action_code:", na.action_code, cta);
      toast.error("Action could not be completed — no destination configured.");
      return;
    }
    if (target.startsWith("/")) { navigate(target); return; }
    if (TAB_KEYS.includes(target)) {
      setTab(target);
      if (!cta.notice) toast.success(`Opening ${na.label}…`);
      setTimeout(() => document.getElementById("ws-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } else {
      setShowBriefing(true);
    }
  };

  // Secondary cockpit CTA handler — routes through the unified dispatcher so all mutation
  // and navigation action_key branches are handled in one place.
  // Previously a standalone handler; split dispatch caused silent no-ops for new action_keys
  // that were only in the primary dispatcher.
  const handleCockpitSecondaryAction = (secondaryAction) => {
    handleCockpitAction(secondaryAction);
  };

  const handleProdOverrideConfirm = async () => {
    if (!prodOverrideConfirm?.engagementId || prodOverrideBusy) return;
    setProdOverrideBusy(true);
    try {
      await api.post(`/client-success/engagements/${prodOverrideConfirm.engagementId}/production-override`);
      toast.success("Production started — Organizer remains open.");
      setProdOverrideConfirm(null);
      // Refresh cockpit so the next action updates to production workspace
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err, "Could not start production — please try again."));
    } finally {
      setProdOverrideBusy(false);
    }
  };

  const handleHandoffConfirm = async () => {
    if (!handoffConfirm?.activationId || handoffBusy) return;
    setHandoffBusy(true);
    try {
      await api.post(`/client-success/activations/${handoffConfirm.activationId}/transfer`, {});
      toast.success("Transferred to Production. Opening Production workspace…");
      setHandoffConfirm(null);
      setTab("production");
      // Refresh cockpit so complete_production_handoff obligation disappears
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = status === 403
        ? "You don't have permission to complete this Production handoff."
        : formatApiErrorDetail(detail) || "Handoff failed — please try again.";
      toast.error(msg);
      // Stay on current page; do NOT navigate away on failure
    } finally {
      setHandoffBusy(false);
    }
  };


  const handleExtCompleteConfirm = async () => {
    if (!extCompleteConfirm?.productionId || extCompleteBusy) return;
    setExtCompleteBusy(true);
    try {
      const r = await api.post(
        `/production/records/${extCompleteConfirm.productionId}/complete-external`,
        {
          completed_at: extCompleteForm.completed_at
            ? new Date(extCompleteForm.completed_at).toISOString()
            : null,
          note: extCompleteForm.note || null,
        }
      );
      // F3 — Phase A: surface the next step explicitly without auto-initiating.
      // The next_action field in the response advises staff to confirm deliverables.
      // Management condition: opening CloseOutPanel must NOT auto-create an initiation
      // record. The "Confirm Deliverables" action below is an explicit staff click
      // that runs the start_close_out flow (which calls initiate then opens the panel).
      const nextAction = r.data?.next_action;
      if (nextAction?.action_code === "initiate_close_out") {
        toast.success("Production recorded as completed.", {
          description: "Next: confirm the documents delivered to the client.",
          action: {
            label: "Confirm Deliverables →",
            onClick: () => {
              // Explicit staff action: initiate close-out then open CloseOutPanel.
              // Mirrors the existing start_close_out Journey-action handler.
              api
                .post(
                  `/production/records/${nextAction.production_id}/close-out/initiate`
                )
                .then(() => {
                  setCloseOutPanel({ productionId: nextAction.production_id });
                  api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
                })
                .catch(() => toast.error("Could not start close-out. Try from the Journey panel."));
            },
          },
          duration: 12000,
        });
      } else {
        toast.success("Production recorded as completed.");
      }
      setExtCompleteConfirm(null);
      api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err, "Could not record external completion — please try again."));
    } finally {
      setExtCompleteBusy(false);
    }
  };

  const handleAiTakeAction = (action) => {    // Governed navigation: execute the destination resolved by lib/aiCommandActions. Opening the
    // workflow is sufficient — nothing is advanced/sent/mutated; human approval remains required.
    const nav = action?.nav;
    if (!nav) { navigate(`/crm/${id}`); return; }
    if (nav.kind === "tab") setTab(nav.value);
    else navigate(nav.value);
  };
  const handleAiTool = (toolName) => {
    const map = {
      DraftEmail: () => setTab("communication"),
      ConversationSummary: () => navigate(`/case-analysis/${id}`),
      NextStepRecommendations: () => navigate(`/case-analysis/${id}`),
      CaseAssist: () => navigate(`/crm/${id}`),
      KnowledgeRetrieval: () => navigate("/ai/knowledge"),
      SchedulingReview: () => quickAction("Scheduling arrives with Communications"),
    };
    (map[toolName] || (() => navigate(`/crm/${id}`)))();
  };

  const portalStatus = c.portal_status || "none";
  const portalInvited = ["invitation_sent", "active", "activated"].includes(portalStatus);

  const invitePortal = async () => {
    const first = portalStatus === "none" || !portalStatus;
    try {
      const { data } = await api.post(`/portal/admin/${id}/${first ? "enable" : "resend"}`);
      setInviteResult(data);
      if (data.email_status === "sent") toast.success(`Invitation emailed to ${data.email}`);
      else if (data.email_status === "suppressed_preview_guardrail") toast.warning("Preview environment: invitation prepared — email suppressed. Share the activation link below.");
      else if (data.email_status === "disabled") toast.warning("Invitation prepared — email provider not configured. Share the activation link below.");
      else if (data.email_status === "error") toast.warning("Invitation prepared — email delivery failed. Share the activation link below.");
      else toast.warning("Invitation prepared — email was not sent. Share the activation link below.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send portal invite");
    }
  };

  const startPortalView = async () => {
    if (pvMode === "interactive" && pvReason.trim().length < 3) {
      toast.error("A reason is required for Interactive Portal View."); return;
    }
    setPvBusy(true);
    try {
      const { data } = await api.post("/portal-view/sessions", {
        client_id: id, mode: pvMode, reason: pvReason.trim() || undefined,
      });
      setPortalView({
        token: data.token, mode: data.mode, session_id: data.session_id,
        subject: data.subject, actor: data.actor,
        reason: pvReason.trim() || undefined,
        deadline_ms: data.absolute_expires_at ? new Date(data.absolute_expires_at).getTime() : null,
        returnTo: window.location.href,
        client_id: id,
      });
      window.open("/portal", "_blank");
      toast.success("Portal View opened in a new tab.");
      setPvOpen(false); setPvReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open Portal View");
    } finally { setPvBusy(false); }
  };

  return (
    <div data-testid="client-workspace-page">
      <div className="min-w-0">
        {/* Conversion banner — auto-dismissed after 8 seconds */}
        {convertedBanner && (
          <div data-testid="ws-converted-banner"
            className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-xl px-4 py-3 mb-4 animate-in slide-in-from-top-2">
            <Icons.UserCheck className="h-4 w-4 shrink-0" />
            <span className="flex-1">
              This lead has been converted to an <strong>Active Client</strong>. The workspace has updated.
            </span>
            <button onClick={() => setConvertedBanner(false)} className="ml-auto text-emerald-600 hover:text-emerald-900">
              <Icons.X className="h-4 w-4" />
            </button>
          </div>
        )}
        {/* Sticky header */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-5 sticky top-0 z-20" data-testid="workspace-header">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              <button onClick={() => navigate("/client-workspace")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="ws-back"><Icons.ArrowLeft className="h-4 w-4" /></button>
              <div className="h-14 w-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-lg font-semibold shrink-0">{initials}</div>
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="font-heading text-2xl font-semibold text-slate-900">{c.full_name || "Unnamed"}</h1>
                  <Pill tone={LIFECYCLE_TONES[c.lifecycle_status] || "neutral"} data-testid="ws-lifecycle-chip">{LIFECYCLE_LABELS[c.lifecycle_status] || "Lead"}</Pill>
                  <GovernanceStatusChip clientId={id} onOpen={() => setTab("provenance")} />
                </div>
                <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs">{c.client_number}</span>
                  <span aria-hidden="true">•</span>
                  <span>Since {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden lg:grid grid-cols-2 gap-5">
                <AssignmentField
                  label="Assigned Team"
                  icon="Users"
                  value={c.assigned_team_name || null}
                  placeholder="+ Assign Team"
                  options={(teams || []).map((t) => ({ id: t.id, name: t.name, subtitle: t.description || null }))}
                  canEdit={canAssign}
                  saving={savingTeam}
                  testid="ws-assign-team"
                  onSave={(tid) => saveTeam(tid)}
                />
                <AssignmentField
                  label="Case Manager"
                  icon="UserCheck"
                  value={c.assigned_case_manager_name || null}
                  placeholder="+ Assign Manager"
                  options={(staffList || []).map((s) => ({ id: s.id, name: s.name, subtitle: [s.title, s.role].filter(Boolean).join(" · ") || null }))}
                  canEdit={canAssign}
                  saving={savingManager}
                  testid="ws-assign-manager"
                  onSave={(mid) => saveManager(mid)}
                />
              </div>
              <PortalStatusIndicator client={c} onPortalAction={(status) => status === "active" && isAdmin ? setPvOpen(true) : invitePortal()} />
              <HealthRing score={intel.health_score || 0} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium" data-testid="ws-quick-actions"><Icons.Zap className="h-4 w-4" />Actions<Icons.ChevronDown className="h-3.5 w-3.5" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* CLIENT ACTIONS */}
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Client Actions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => { setTab("communication"); setOpenComposer(true); setTimeout(() => document.getElementById("ws-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120); }} data-testid="ws-send-message"><Icons.Send className="h-4 w-4 mr-2" />Send Message</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => quickAction("Scheduling arrives with Communications")} data-testid="ws-schedule-meeting"><Icons.Calendar className="h-4 w-4 mr-2" />Schedule Meeting</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowTaskModal(true)} data-testid="ws-create-task"><Icons.ClipboardCheck className="h-4 w-4 mr-2" />Create Staff Task</DropdownMenuItem>
                  <DropdownMenuItem onClick={invitePortal} data-testid="ws-invite-portal"><Icons.ShieldCheck className="h-4 w-4 mr-2" />{portalInvited ? "Resend Portal Invite" : "Invite to Secure Portal"}</DropdownMenuItem>
                  {isAdmin && portalStatus === "active" && (
                    <DropdownMenuItem onClick={() => setPvOpen(true)} data-testid="ws-view-portal">
                      <Icons.Eye className="h-4 w-4 mr-2" />Open Portal View
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {/* WORKFLOW ACTIONS */}
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Workflow Actions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate(`/sales/estimates?create=1&client=${id}`)} data-testid="ws-create-estimate"><Icons.FileSignature className="h-4 w-4 mr-2" />Create Estimate</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowCreateEngDialog(true)} data-testid="ws-create-engagement"><Icons.FileCheck className="h-4 w-4 mr-2" />Create Engagement</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => quickAction("Organizers arrive with Client Success")}><Icons.ClipboardList className="h-4 w-4 mr-2" />Assign Organizer</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTab("documents")} data-testid="ws-request-documents"><Icons.FileText className="h-4 w-4 mr-2" />Request Documents</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/documents")}><Icons.Upload className="h-4 w-4 mr-2" />Upload Documents</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* AI ASSISTANCE */}
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">AI Assistance</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate(`/case-analysis/${id}`)} data-testid="ws-tool-next-steps"><Icons.ListChecks className="h-4 w-4 mr-2" />Next Steps</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowBriefing(true)} data-testid="ws-ai-briefing"><Icons.Sparkles className="h-4 w-4 mr-2 text-blue-600" />AI Briefing</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/case-analysis/${id}`)} data-testid="ws-ai-case-analysis"><Icons.Brain className="h-4 w-4 mr-2 text-blue-600" />AI Case Analysis</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/crm/${id}`)} data-testid="ws-tool-case-assist"><Icons.Sparkles className="h-4 w-4 mr-2 text-blue-600" />Case Assist</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTab("communication")} data-testid="ws-tool-draft-email"><Icons.Mail className="h-4 w-4 mr-2" />Draft Email</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/ai/knowledge")} data-testid="ws-tool-knowledge"><Icons.BookOpen className="h-4 w-4 mr-2" />Knowledge Center</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* NAVIGATION & ACCOUNT */}
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Navigation &amp; Account</DropdownMenuLabel>
                  {/* Workspaces — flyout submenu; context-aware STR launch */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid="ws-workspaces-trigger">
                      <Icons.LayoutDashboard className="h-4 w-4 mr-2" />Workspaces
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-52">
                      <DropdownMenuItem disabled className="text-xs text-slate-400 italic">
                        <Icons.Home className="h-3.5 w-3.5 mr-2 opacity-50" />Client Workspace (current)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Advisory Services</DropdownMenuLabel>
                      <DropdownMenuItem
                        data-testid="ws-open-str-workspace"
                        onClick={() => {
                          // Direct open when a workspace already exists; dialog when not.
                          const hasWorkspace = strState && strState.action_code !== "create_str_estimate";
                          if (hasWorkspace) {
                            navigate(resolveStrTarget(strState, id));
                          } else {
                            setShowStrDialog(true);
                          }
                        }}>
                        <Icons.Compass className="h-4 w-4 mr-2" />Strategic Review
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="ws-open-thai-tax-workspace"
                        onClick={() => {
                          if (thaiTaxState?.has_organizer) {
                            navigate(thaiTaxState.workspace_target);
                          } else {
                            setShowThaiTaxDialog(true);
                          }
                        }}>
                        <Icons.FileText className="h-4 w-4 mr-2" />Thai Tax Return
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuItem onClick={() => navigate(`/crm/${id}`)}><Icons.IdCard className="h-4 w-4 mr-2" />View CRM Record</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="ws-close-matter"
                    className="text-red-600 focus:text-red-700 focus:bg-red-50"
                    onClick={() => setShowCloseMatterDialog(true)}
                  >
                    <Icons.XCircle className="h-4 w-4 mr-2 text-red-500" />Close Matter
                  </DropdownMenuItem>

                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* ── Operational Cockpit — the ONE authoritative operational summary ── */}
        <WorkspaceOperationalBoundary onRetry={handleDrlRefresh}>
          <OperationalCockpit
            data={cockpit}
            drlData={drlData}
            clientId={id}
            onPrimaryAction={handleCockpitAction}
            onSecondaryAction={handleCockpitSecondaryAction}
            onViewBriefing={() => setShowBriefing(true)}
            onViewRouting={() => setTab("workflow")}
            onResolveObligation={() => setShowResolveObligationDialog(true)}
            onRefresh={handleDrlRefresh}
            onDocumentAction={handleDocumentBlocker}
          />
        </WorkspaceOperationalBoundary>

        {/* Left grouped navigation + workspace content */}
        <div id="ws-tabs" className="flex gap-5 items-start scroll-mt-4">
          <WorkspaceNav tab={tab} setTab={setTab} />
          <div className="flex-1 min-w-0">
            <WorkspaceTabs bundle={bundle} related={related} documents={documents} timeline={timeline} audit={audit} clientId={id} reload={load} tab={tab} setTab={setTab} engSection={engSection} setEngSection={setEngSection} openComposer={openComposer} onOpenComposerHandled={() => setOpenComposer(false)} cockpit={cockpit} autoOpenEngId={searchParams.get("engagement_id")} autoOpenAction={searchParams.get("action")} drlData={drlData} drlLoading={drlLoading} runDrl={runDrl} onDocumentAction={handleDocumentBlocker} />
          </div>
        </div>
      </div>

      <ResolveEstimateObligationDialog
        open={showResolveObligationDialog}
        onClose={() => setShowResolveObligationDialog(false)}
        clientId={id}
        engagementId={cockpit?.primary_engagement_id}
        engagementNumber={cockpit?.primary_engagement_number}
        estimateNumber={cockpit?.estimate_lifecycle?.estimate_number}
        onResolved={() => {
          setShowResolveObligationDialog(false);
          // Reload cockpit + workspace bundle so all surfaces recompute
          api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
          load();
        }}
      />

      {/* ── Close Matter / Client Not Proceeding ── */}
      <CloseMatterDialog
        open={showCloseMatterDialog}
        onOpenChange={setShowCloseMatterDialog}
        clientId={id}
        onSuccess={() => {
          // Reload all workspace surfaces after closure
          api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
          load();
        }}
      />



      {/* ── P0: Governed Document Blocker Resolution Panel ── */}
      <ResolveDocumentBlockerPanel
        open={documentBlockerPanelOpen}
        onClose={() => setDocumentBlockerPanelOpen(false)}
        clientId={id}
        engagementId={drlData?.scope_engagement_id || cockpit?.primary_engagement_id}
        drlData={drlData}
        runDrl={runDrl}
        onRefreshCockpit={handleCockpitRefreshFromBlockerPanel}
        apiBase={API}
        token={getToken()}
      />

      {/* Create Staff Task modal — strictly internal, client never sees this */}
      {showTaskModal && (
        <CreateStaffTaskModal
          clientId={id}
          clientName={c?.full_name || bundle?.lead?.full_name || ""}
          currentUser={user}
          staffList={staffList}
          onClose={() => setShowTaskModal(false)}
          onCreated={load}
        />
      )}

      <Dialog open={showBriefing} onOpenChange={setShowBriefing}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="ws-briefing-dialog">
          <DialogHeader><DialogTitle>AI Briefing — Executive Summary</DialogTitle></DialogHeader>
          <AICommandCard
            buildContext={() => ({
              conversation_id: id, lead_client_id: id,
              case_stage: wf?.state?.current_stage || undefined,
              client_goals: [], open_questions: [], known_decisions: [],
            })}
            workflowId={wf?.state?.id}
            assignedTeam={c.assigned_team_name || null}
            routing={intel.routing_recommendation}
            bottleneck={bundle.onboarding}
            clientId={id}
            journeyAction={intel.journey?.recommended_action}
            onTakeAction={handleAiTakeAction}
            onToolClick={handleAiTool}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteResult} onOpenChange={(o) => !o && setInviteResult(null)}>
        <DialogContent data-testid="ws-invite-dialog">
          <DialogHeader><DialogTitle>Secure Portal Invitation</DialogTitle></DialogHeader>
          {inviteResult && (
            <div className="space-y-3">
              {inviteResult.email_status === "sent" ? (
                <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3">
                  <Icons.MailCheck className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Invitation email sent to <strong>{inviteResult.email}</strong>.</span>
                </div>
              ) : inviteResult.email_status === "suppressed_preview_guardrail" ? (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                  <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Preview environment: email delivery to real addresses is suppressed. Copy the secure activation link below and share it with {inviteResult.email || "the client"} directly.</span>
                </div>
              ) : inviteResult.email_status === "disabled" ? (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                  <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Email provider is not configured (no API key). Copy the secure activation link below and share it with {inviteResult.email || "the client"} directly.</span>
                </div>
              ) : inviteResult.email_status === "error" ? (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg p-3">
                  <Icons.XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Email delivery failed. The activation link below is still valid — copy and share it with {inviteResult.email || "the client"} directly. Contact your administrator if the issue persists.</span>
                </div>
              ) : (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                  <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Email was not sent ({inviteResult.email_status || "unknown reason"}). The activation link below is still valid — copy and share it with {inviteResult.email || "the client"} directly.</span>
                </div>
              )}
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">Activation link</div>
                {inviteResult.activation_url ? (
                  <div className="flex items-center gap-2">
                    <input readOnly value={inviteResult.activation_url} data-testid="ws-invite-link"
                      className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono bg-slate-50" onFocus={(e) => e.target.select()} />
                    <button data-testid="ws-invite-copy" onClick={() => { navigator.clipboard?.writeText(inviteResult.activation_url); toast.success("Link copied"); }}
                      className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 text-sm font-medium shrink-0"><Icons.Copy className="h-4 w-4" />Copy</button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic" data-testid="ws-invite-link-unavailable">
                    {inviteResult.email_status === "sent"
                      ? "Activation link delivered to the client via email."
                      : "Activation link unavailable — please resend the invitation."}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* STR — no workspace dialog: shown when client has no Strategic Review */}
      <Dialog open={showStrDialog} onOpenChange={setShowStrDialog}>
        <DialogContent className="max-w-sm" data-testid="ws-str-no-workspace-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.Compass className="h-5 w-5 text-blue-600" />Strategic Review Workspace
            </DialogTitle>
          </DialogHeader>
          <div className="pt-1 space-y-4">
            <p className="text-sm text-slate-600">
              This client does not currently have a Strategic Tax Review.
            </p>
            <div className="space-y-2">
              <button
                data-testid="ws-str-create-new"
                onClick={() => {
                  setShowStrDialog(false);
                  navigate(`/sales/estimates?create=1&client=${id}&type=str`);
                }}
                className="w-full flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 px-4 py-3 text-sm font-medium text-blue-800 transition-colors">
                <Icons.FilePlus2 className="h-4 w-4 shrink-0" />
                Create New Strategic Review
              </button>
              <button
                data-testid="ws-str-dialog-cancel"
                onClick={() => setShowStrDialog(false)}
                className="w-full flex items-center gap-3 rounded-lg border border-slate-200 hover:bg-slate-50 px-4 py-3 text-sm text-slate-600 transition-colors">
                <Icons.X className="h-4 w-4 shrink-0" />
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Thai Tax Return — no organizer dialog */}
      <Dialog open={showThaiTaxDialog} onOpenChange={setShowThaiTaxDialog}>
        <DialogContent className="max-w-sm" data-testid="ws-thai-tax-no-organizer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.FileText className="h-5 w-5 text-amber-600" />Thai Tax Return Workspace
            </DialogTitle>
          </DialogHeader>
          <div className="pt-1 space-y-4">
            <p className="text-sm text-slate-600">
              This client does not currently have a Thai Tax organizer. Create a Thai Tax
              estimate to begin the engagement workflow.
            </p>
            <div className="space-y-2">
              <button
                data-testid="ws-thai-tax-create-estimate"
                onClick={() => {
                  setShowThaiTaxDialog(false);
                  navigate(`/sales/estimates?create=1&client=${id}&type=thai_tax`);
                }}
                className="w-full flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 px-4 py-3 text-sm font-medium text-amber-800 transition-colors">
                <Icons.FilePlus2 className="h-4 w-4 shrink-0" />
                Create Thai Tax Estimate
              </button>
              <button
                data-testid="ws-thai-tax-dialog-cancel"
                onClick={() => setShowThaiTaxDialog(false)}
                className="w-full flex items-center gap-3 rounded-lg border border-slate-200 hover:bg-slate-50 px-4 py-3 text-sm text-slate-600 transition-colors">
                <Icons.X className="h-4 w-4 shrink-0" />
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-up / Action Dialog — handles estimate reminder, engagement reminder, prepare & send engagement */}
      <Dialog open={!!followUpConfirm} onOpenChange={(o) => !o && !followUpBusy && setFollowUpConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="ws-followup-estimate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {followUpConfirm?.type === "prepare_engagement" ? (
                <><Icons.FileText className="h-5 w-5 text-emerald-600" />Prepare Engagement Letter</>
              ) : followUpConfirm?.type === "send_engagement" ? (
                <><Icons.Send className="h-5 w-5 text-emerald-600" />Send Engagement Letter</>
              ) : followUpConfirm?.type === "engagement" ? (
                <><Icons.Bell className="h-5 w-5 text-blue-600" />Send Engagement Letter Reminder</>
              ) : (
                <><Icons.Bell className="h-5 w-5 text-blue-600" />Send Estimate Reminder</>
              )}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mt-1">
            {followUpConfirm?.type === "prepare_engagement" ? (
              followUpConfirm?.isHistorical
                ? <>Prepare the engagement letter for <strong>{followUpConfirm?.clientName || "this client"}</strong>. Since this is a historical acceptance, the letter will be created for staff review — you can then send it manually.</>
                : <>Prepare and send the engagement letter for <strong>{followUpConfirm?.clientName || "this client"}</strong> for e-signature. This re-runs the canonical engagement automation.</>
            ) : followUpConfirm?.type === "send_engagement" ? (
              <>Send the drafted engagement letter to <strong>{followUpConfirm?.clientName || "the client"}</strong> for e-signature. They will receive an email with a secure signing link.
                {/* Preview affordance — resolved lazily from unsigned_document_id */}
                <span className="block mt-2">
                  {previewDocLoading ? (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                      <Icons.Loader2 className="h-3 w-3 animate-spin" />Loading preview…
                    </span>
                  ) : previewDocId ? (
                    <a
                      href={`${API}/files/${previewDocId}/download?auth=${getToken()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                      data-testid="ws-preview-engagement-pdf">
                      <Icons.FileText className="h-3 w-3" />Preview Engagement Letter
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">No PDF preview available</span>
                  )}
                </span>
              </>
            ) : followUpConfirm?.type === "engagement" ? (
              <>Send <strong>{followUpConfirm?.clientName || "the client"}</strong> a reminder to review and sign their engagement letter? They will receive an email with a secure signing link.</>
            ) : (
              <>Send <strong>{followUpConfirm?.clientName || "the client"}</strong> a reminder to review and accept the estimate? They will receive an email with a secure link to their portal.</>
            )}
          </p>
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => !followUpBusy && setFollowUpConfirm(null)}
              disabled={followUpBusy}
              className="flex-1 h-9 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              data-testid="ws-followup-cancel">
              Cancel
            </button>
            <button
              onClick={
                followUpConfirm?.type === "prepare_engagement" ? prepareEngagementLetter
                  : followUpConfirm?.type === "send_engagement" ? sendEngagementLetterDirect
                  : followUpConfirm?.type === "engagement" ? sendEngagementLetterReminder
                  : sendEstimateReminder
              }
              disabled={followUpBusy}
              className="flex-1 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="ws-followup-send">
              {followUpBusy
                ? <><Icons.Loader2 className="h-4 w-4 animate-spin" />Processing…</>
                : followUpConfirm?.type === "prepare_engagement"
                  ? <><Icons.FileText className="h-4 w-4" />Prepare Letter</>
                  : followUpConfirm?.type === "send_engagement"
                    ? <><Icons.Send className="h-4 w-4" />Send Letter</>
                    : <><Icons.Send className="h-4 w-4" />Send Reminder</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Production Handoff confirmation dialog */}
      <Dialog open={!!handoffConfirm} onOpenChange={(o) => !o && !handoffBusy && setHandoffConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="handoff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.Send className="h-5 w-5 text-slate-600" />
              Complete Production Handoff?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 leading-relaxed py-1">
            This will transfer the engagement from Client Success to Production. A production record will be created and the Production team will be notified. If a production record already exists, it will be reconciled safely.
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => !handoffBusy && setHandoffConfirm(null)}
              disabled={handoffBusy}
              data-testid="handoff-cancel"
              className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleHandoffConfirm}
              disabled={handoffBusy}
              data-testid="handoff-confirm"
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            >
              {handoffBusy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}
              Transfer to Production
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Production Organizer Override confirmation dialog */}
      <Dialog open={!!prodOverrideConfirm} onOpenChange={(o) => !o && !prodOverrideBusy && setProdOverrideConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="prod-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.ArrowRightCircle className="h-5 w-5 text-slate-600" />
              Proceed to Production?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 leading-relaxed py-1">
            The client's Organizer is not yet complete. Production can begin now while the Organizer remains open for the client to finish later.
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => !prodOverrideBusy && setProdOverrideConfirm(null)}
              disabled={prodOverrideBusy}
              data-testid="prod-override-cancel"
              className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleProdOverrideConfirm}
              disabled={prodOverrideBusy}
              data-testid="prod-override-confirm"
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            >
              {prodOverrideBusy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}
              Proceed to Production
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* External Production Completion dialog */}
      <Dialog open={!!extCompleteConfirm} onOpenChange={(o) => !o && !extCompleteBusy && setExtCompleteConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="ext-complete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icons.CheckCircle className="h-5 w-5 text-slate-600" />
              Record Production as Completed?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-600 leading-relaxed py-1">
            <p>Use this when the work for this engagement was completed outside Tax Flow. Tax Flow will record the production work as completed and advance the engagement toward its remaining completion steps.</p>
            <p className="text-xs text-slate-500">Outstanding organizer or document requests will not be falsely marked as completed.</p>
          </div>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Completion Method</label>
              <div className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm">Completed outside Tax Flow</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Completion Date</label>
              <input
                type="date"
                value={extCompleteForm.completed_at}
                onChange={(e) => setExtCompleteForm((f) => ({ ...f, completed_at: e.target.value }))}
                data-testid="ext-complete-date"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                Note <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea
                rows={3}
                value={extCompleteForm.note}
                onChange={(e) => setExtCompleteForm((f) => ({ ...f, note: e.target.value }))}
                data-testid="ext-complete-note"
                placeholder="e.g. Strategic Tax Review prepared outside Tax Flow and emailed directly to client."
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-none"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => !extCompleteBusy && setExtCompleteConfirm(null)}
              disabled={extCompleteBusy}
              data-testid="ext-complete-cancel"
              className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExtCompleteConfirm}
              disabled={extCompleteBusy}
              data-testid="ext-complete-confirm"
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            >
              {extCompleteBusy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}
              Record Production Completed
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close-Out Deliverables Panel */}
      {closeOutPanel && (
        <CloseOutPanel
          clientId={id}
          productionId={closeOutPanel.productionId}
          onClose={() => setCloseOutPanel(null)}
          onConfirmed={() => { setCloseOutPanel(null); load(); }}
        />
      )}

      {/* Create Engagement dialog — governed commercial-scope selection */}      <CreateEngagementDialog
        open={showCreateEngDialog}
        onOpenChange={(o) => { setShowCreateEngDialog(o); if (!o) setEngOptions(null); }}
        clientId={id}
        clientName={c?.full_name}
        engOptions={engOptions}
        setEngOptions={setEngOptions}
        engOptionsLoading={engOptionsLoading}
        setEngOptionsLoading={setEngOptionsLoading}
        onPrepare={(opt) => {
          setShowCreateEngDialog(false);
          setEngOptions(null);
          setFollowUpConfirm({
            type: "prepare_engagement",
            estimateId: opt.estimate_id,
            isHistorical: opt.is_historical_acceptance,
            clientName: c?.full_name,
          });
        }}
        onOpen={() => {
          setShowCreateEngDialog(false);
          setEngOptions(null);
          setTab("engagements");
          setEngSection("letters");
          setTimeout(() => document.getElementById("ws-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
        }}
      />

      {/* ── Portal View launch dialog (mirrors PortalAdminCard, exits back here via returnTo) */}
      <Dialog open={pvOpen} onOpenChange={setPvOpen}>
        <DialogContent data-testid="ws-portal-view-dialog">
          <DialogHeader>
            <DialogTitle>Open Portal View</DialogTitle>
            <DialogDescription>
              You will view this client&rsquo;s portal as yourself — you are never logged in as the client. All actions are audited.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={pvMode} onValueChange={setPvMode} className="space-y-2">
            <label htmlFor="ws-pv-preview" className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${pvMode === "preview" ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}>
              <RadioGroupItem value="preview" id="ws-pv-preview" className="mt-1" data-testid="ws-portal-view-mode-preview" />
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5"><Icons.Eye className="h-4 w-4 text-amber-600" />Read-Only Preview</div>
                <div className="text-xs text-slate-500">See exactly what the client sees. All actions are disabled.</div>
              </div>
            </label>
            <label htmlFor="ws-pv-interactive" className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${pvMode === "interactive" ? "border-rose-400 bg-rose-50" : "border-slate-200"}`}>
              <RadioGroupItem value="interactive" id="ws-pv-interactive" className="mt-1" data-testid="ws-portal-view-mode-interactive" />
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5"><Icons.PenLine className="h-4 w-4 text-rose-600" />Interactive (Act on Behalf)</div>
                <div className="text-xs text-slate-500">Assist the client with governed actions. Signatures, payments, and identity verification remain blocked.</div>
              </div>
            </label>
          </RadioGroup>
          <div className="space-y-1.5">
            <Label htmlFor="ws-pv-reason" className="text-xs">
              Reason {pvMode === "interactive" ? <span className="text-rose-600">(required)</span> : "(optional)"}
            </Label>
            <Textarea id="ws-pv-reason" value={pvReason} onChange={(e) => setPvReason(e.target.value)}
              placeholder="e.g. Helping the client complete their organizer over a support call"
              rows={2} data-testid="ws-portal-view-reason" />
          </div>
          <DialogFooter>
            <ActionButton variant="ghost" onClick={() => setPvOpen(false)} data-testid="ws-portal-view-cancel">Cancel</ActionButton>
            <ActionButton icon="ExternalLink" loading={pvBusy} onClick={startPortalView} data-testid="ws-portal-view-start">
              Open {pvMode === "interactive" ? "Interactive View" : "Preview"}
            </ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STR External Delivery modal — Upload Completed STR / View Delivery Status */}
      <StrExternalDeliveryModal
        open={showStrExternalDelivery}
        onClose={() => setShowStrExternalDelivery(false)}
        clientId={id}
        clientName={c?.full_name}
        onDelivered={() => {
          setShowStrExternalDelivery(false);
          // Refresh cockpit so Next Recommended Action updates to delivery state
          api.get(`/workspace/${id}/cockpit`).then((ck) => setCockpit(ck.data)).catch(() => {});
        }}
      />

    </div>
  );
}

// ── Create Engagement dialog ────────────────────────────────────────────────
// Governed commercial-scope selection for the Actions → Create Engagement action.
// Never infers a service from client profile, team, or recommendations.
// Reuses the existing prepare-engagement mutation endpoint (no duplicate generator).
function CreateEngagementDialog({ open, onOpenChange, clientId, clientName, engOptions, setEngOptions, engOptionsLoading, setEngOptionsLoading, onPrepare, onOpen }) {
  const { user } = useAuth();
  useEffect(() => {
    if (!open) return;
    setEngOptionsLoading(true);
    api.get(`/sales/estimates/engagement-options/${clientId}`)
      .then(r => setEngOptions(r.data || []))
      .catch(() => setEngOptions([]))
      .finally(() => setEngOptionsLoading(false));
  }, [open, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const ENG_STATUS_LABELS = {
    draft: "Draft", generated: "Prepared", sent: "Sent", viewed: "Viewed",
    signed: "Signed", active: "Active",
  };
  const ENG_STATUS_COLORS = {
    draft: "bg-slate-100 text-slate-600",
    generated: "bg-amber-50 text-amber-700",
    sent: "bg-blue-50 text-blue-700",
    viewed: "bg-blue-50 text-blue-700",
    signed: "bg-emerald-50 text-emerald-700",
    active: "bg-emerald-50 text-emerald-700",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="create-engagement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.FileCheck className="h-5 w-5 text-slate-600" />
            Create Engagement
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">
          Select an accepted commercial scope to create an engagement letter for{" "}
          <strong>{clientName || "this client"}</strong>.
        </p>

        {engOptionsLoading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
            <Icons.Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading accepted scope…</span>
          </div>
        ) : !engOptions || engOptions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center" data-testid="create-engagement-no-scope">
            <Icons.FileX className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-600">No accepted commercial scope</p>
            <p className="text-xs text-slate-400 mt-1">Create and accept an estimate first to generate an engagement letter.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="create-engagement-options">
            {engOptions.map((opt) => {
              const hasEng = Boolean(opt.existing_engagement);
              const engStatus = opt.existing_engagement?.engagement_status;
              return (
                <div
                  key={opt.estimate_id}
                  className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:bg-slate-50 transition-colors"
                  data-testid={`eng-option-${opt.estimate_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-slate-400">{opt.estimate_number}</span>
                      {hasEng && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${ENG_STATUS_COLORS[engStatus] || "bg-slate-100 text-slate-600"}`}>
                          {opt.existing_engagement.engagement_number} · {ENG_STATUS_LABELS[engStatus] || engStatus}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-800 mt-0.5 truncate">{opt.title}</p>
                    {opt.services?.length > 0 && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate">
                        {opt.services.map(s => s.name).filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-0.5">
                      {new Set(["administrator", "partner", "billing"]).has(user?.role) && (
                        <>
                          {opt.currency} {(opt.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </>
                      )}
                      {opt.accepted_at && (
                        <span className="text-slate-400">{new Set(["administrator", "partner", "billing"]).has(user?.role) ? " · " : ""}accepted {new Date(opt.accepted_at).toLocaleDateString()}</span>
                      )}
                    </p>
                  </div>
                  <div className="shrink-0">
                    {hasEng ? (
                      <button
                        onClick={onOpen}
                        className="h-8 px-3 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors flex items-center gap-1.5"
                        data-testid={`eng-option-open-${opt.estimate_id}`}
                      >
                        <Icons.ExternalLink className="h-3.5 w-3.5" />Open
                      </button>
                    ) : (
                      <button
                        onClick={() => onPrepare(opt)}
                        className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors flex items-center gap-1.5"
                        data-testid={`eng-option-create-${opt.estimate_id}`}
                      >
                        <Icons.FilePlus2 className="h-3.5 w-3.5" />Create
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button
            onClick={() => onOpenChange(false)}
            className="h-8 px-4 rounded-md border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors"
            data-testid="create-engagement-cancel">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
